import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';


const LoginForm = ({ onSubmit, onSwitchToRegister, onForgotPassword }) => {
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    rememberMe: false
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.identifier) {
      newErrors.identifier = 'Username or email is required';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      // Check if identifier is email or username
      const isEmail = formData?.identifier?.includes('@');
      const email = String(formData?.identifier || '').trim();
      const password = String(formData?.password || '');

      // If username provided, we need to get email (for now, treat as email)
      // In production, you'd query user_profiles to get email from username
      if (!isEmail) {
        setErrors({ identifier: 'Please use your email address to login' });
        setIsLoading(false);
        return;
      }

      if (!email || !password.trim()) {
        setErrors({ password: 'Email and password are required' });
        setIsLoading(false);
        return;
      }

      const { error } = await signIn(email, password);

      if (error) {
        setErrors({ password: error?.message || 'Invalid email or password' });
      } else {
        // Success - AuthContext will handle navigation
        if (onSubmit) onSubmit(formData);
      }
    } catch (error) {
      setErrors({ password: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        type="text"
        label="Username or Email"
        placeholder="Enter your username or email"
        value={formData?.identifier}
        onChange={(e) => handleInputChange('identifier', e?.target?.value)}
        error={errors?.identifier}
        description="Use the credentials you created during registration"
        required
        disabled={isLoading}
      />
      <div className="relative">
        <Input
          type={showPassword ? 'text' : 'password'}
          label="Password"
          placeholder="Enter your password"
          value={formData?.password}
          onChange={(e) => handleInputChange('password', e?.target?.value)}
          error={errors?.password}
          required
          disabled={isLoading}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
          disabled={isLoading}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
        </button>
      </div>
      <div className="flex items-center justify-between">
        <Checkbox
          label="Remember me"
          checked={formData?.rememberMe}
          onChange={(e) => handleInputChange('rememberMe', e?.target?.checked)}
          disabled={isLoading}
        />
        <button
          type="button"
          onClick={onForgotPassword}
          className="text-sm text-primary hover:underline font-medium"
          disabled={isLoading}
        >
          Forgot password?
        </button>
      </div>
      <Button
        type="submit"
        variant="default"
        fullWidth
        iconName="LogIn"
        iconPosition="left"
        disabled={isLoading}
      >
        {isLoading ? 'Signing In...' : 'Sign In'}
      </Button>
      <div className="text-center">
        <button
          type="button"
          onClick={onSwitchToRegister}
          className="text-sm text-primary hover:underline font-medium"
          disabled={isLoading}
        >
          Don't have an account? Create one
        </button>
      </div>
    </form>
  );
};

export default LoginForm;